<template>
  <div class="principal-add-workspace" ref="workspaceTop">
    <article class="lobby-counter-card" v-if="hasLobbyWaiting">
      <div class="lobby-counter-copy">
        <span class="lobby-counter-icon">
          <i class="material-icons">pending_actions</i>
        </span>
        <div>
          <label class="active teal-text">Current Lobby</label>
          <h6>{{ lobbyCount }} principal{{ lobbyCount == 1 ? "" : "s" }} waiting</h6>
        </div>
      </div>

      <button type="button" class="lobby-counter-action" @click.prevent="goToLobby">
        <span>Open Lobby</span>
        <i class="material-icons">arrow_forward</i>
      </button>
    </article>

    <div class="card z-depth-0 add-panel">
      <div class="card-content">
        <div class="add-panel-head">
          <div>
            <label class="active teal-text">Principal Addition</label>
            <h5>Add principals for billing</h5>
          </div>

          <div class="add-switch">
            <a
              href="#"
              :class="{ active: type == 'single' }"
              @click.prevent="type = 'single'"
            >
              Single
            </a>
            <a
              href="#"
              :class="{ active: type == 'bulk' }"
              @click.prevent="type = 'bulk'"
            >
              Bulk
            </a>
          </div>
        </div>

        <EnrolleeSingleDiv
          :cat="cat"
          :clientId="clientId"
          @complete="handleComplete"
          v-if="type == 'single'"
        />
        <EnrolleeBulkDiv
          :cat="cat"
          :clientId="clientId"
          @complete="handleComplete"
          v-if="type == 'bulk'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from "vue";
import getItemsOfItems from "@/composables/getItemsOfItems";
import EnrolleeSingleDiv from "@/components/client/onboard/EnrolleeSingleOnboardComponent.vue";
import EnrolleeBulkDiv from "@/components/client/onboard/EnrolleeBulkOnboardComponent.vue";

export default {
  name: "EnrolleeOnboardComponent",
  components: { EnrolleeSingleDiv, EnrolleeBulkDiv },
  props: ["cat", "clientId"],
  emits: ["lobby"],
  setup(props, context) {
    const type = ref("single");
    const lobbyResponse = ref(null);
    const isLobbyLoading = ref(false);
    const lobbyError = ref(null);
    const workspaceTop = ref(null);

    const getLobbyPrincipals = async () => {
      isLobbyLoading.value = true;
      const { res, err } = await getItemsOfItems(
        "principal/client/search",
        `${props.clientId}?z=p`
      );

      if (res.value) {
        lobbyResponse.value = res.value.data;
      } else {
        lobbyError.value = err.value;
      }

      isLobbyLoading.value = false;
    };

    onMounted(() => {
      getLobbyPrincipals();
    });

    const handleComplete = async () => {
      await getLobbyPrincipals();
      await nextTick();
      const principalLinks = document.querySelector(".client-submode-strip");

      (principalLinks || workspaceTop.value)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    const goToLobby = () => {
      context.emit("lobby");
    };

    const lobbyPrincipals = computed(() => {
      const data = Array.isArray(lobbyResponse.value?.data)
        ? lobbyResponse.value.data
        : Array.isArray(lobbyResponse.value)
          ? lobbyResponse.value
          : [];

      return data.slice(0, 8);
    });

    const lobbyCount = computed(() => {
      return lobbyResponse.value?.total || lobbyPrincipals.value.length;
    });

    const hasLobbyWaiting = computed(() => {
      return Number(lobbyCount.value) > 0;
    });

    return {
      handleComplete,
      goToLobby,
      type,
      workspaceTop,
      lobbyPrincipals,
      lobbyCount,
      hasLobbyWaiting,
      isLobbyLoading,
      lobbyError,
    };
  },
};
</script>

<style scoped>
.principal-add-workspace {
  display: grid;
  gap: 16px;
  margin-top: 8px;
}

.lobby-counter-card,
.add-panel {
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72);
}

.add-panel-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.add-panel-head h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.lobby-counter-card {
  align-items: center;
  background: #ffffff;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 16px 18px;
}

.lobby-counter-copy {
  align-items: center;
  display: flex;
  gap: 12px;
}

.lobby-counter-icon {
  align-items: center;
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-radius: 50%;
  color: #00897b;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.lobby-counter-icon i {
  font-size: 20px;
}

.lobby-counter-card h6 {
  color: #263238;
  font-weight: 800;
  margin: 3px 0 0;
}

.lobby-counter-action {
  align-items: center;
  background: #263238;
  border: 1px solid #263238;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.lobby-counter-action:hover {
  background: #00897b;
  border-color: #00897b;
  box-shadow: 0 10px 18px -18px rgba(0, 105, 92, 0.72);
  transform: translateY(-1px);
}

.lobby-counter-action i {
  font-size: 18px;
}

.add-panel {
  background: #ffffff;
}

.add-panel-head {
  border-bottom: 1px solid #d5e1e5;
  margin-bottom: 20px;
  padding-bottom: 18px;
}

.add-panel :deep(.single-add-card),
.add-panel :deep(.bulk-add-card),
.add-panel :deep(.card-panel),
.add-panel :deep(.card.grey.lighten-5) {
  border: 1px solid #d5e1e5;
  border-radius: 6px;
  box-shadow: 0 12px 22px -28px rgba(38, 50, 56, 0.68);
}

.add-panel :deep(.card-panel) {
  background: #ffffff;
}

.add-switch {
  background: #eceff1;
  border-radius: 18px;
  display: inline-flex;
  padding: 4px;
}

.add-switch a {
  border-radius: 14px;
  color: #455a64;
  font-weight: 700;
  padding: 7px 14px;
}

.add-switch a.active {
  background: #263238;
  color: #ffffff;
}

@media (max-width: 600px) {
  .lobby-counter-card,
  .add-panel-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .lobby-counter-action {
    justify-content: center;
    width: 100%;
  }
}
</style>
